var Repeater = require("enyo-ui/Repeater"),
    ScrollRepeater,
    Scroller = require("enyo-ui/Scroller"),
    kind     = require("enyo/kind"),
    utils    = require("enyo/utils");

require("enyo-ui");

/**
 * [ScrollRepeater]{@link enyo-ui.ScrollRepeater} extends [Repeater]{@link
 * enyo-ui.Repeater} with a [Scroller]{@link enyo-ui.Scroller} wrapped around
 * the arrangement.
 *
 * ScrollRepeater takes care of automatically keeping the same index aligned at
 * the top and/or left side of the viewport on resize and exposes some utility
 * methods to easily snap/scroll to a particular index in the grid.
 *
 * @ui
 * @class   enyo-ui.ScrollRepeater
 * @extends enyo-ui.Repeater
 * @public
 */
ScrollRepeater = module.exports = kind(/** @lends enyo-ui.ScrollRepeater.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.ScrollRepeater",

    /**
     * @private
     */
    kind: Repeater,

    /**
     * @private
     */
    classes: "enyo-ui-scroll-repeater",

    /**
     * Whether to expand the repeater to fit it's container.
     *
     * @type     {boolean}
     * @default  false
     * @public
     */
    fit: false,

    /**
     * The kind responsible for scrolling.
     *
     * @type {enyo-ui.Scroller}
     * @public
     */
    scrollerKind: Scroller,

    /**
     * Extra options to mix into the scroller instance.
     *
     * @type {Object=}
     * @public
     */
    scrollerOptions: null,

    /**
     * Define a selector for the component that will serve as container for
     * the repeater children.
     *
     * @type    {string}
     * @default ".$.childContainer"
     * @public
     */
    childContainerPath: ".$.childContainer",

    /**
     * The index the child closest to the top-left corner of the viewport.
     *
     * @private
     */
    anchor: {
        index:      0,
        offsetLeft: 0,
        offsetTop:  0
    },

    /**
     * @private
     */
    bindings: [{
        from: ".$.scroller.dimensions",
        to:   ".scrollerDimensions"
    }],

    /**
     * @private
     */
    handlers: {
        onScrollStart: "onScrollStart",
        onScroll:      "onScroll",
        onScrollStop:  "onScrollStop"
    },

    // Initialization
    // --------------------------------

    /**
     * @method
     * @private
     */
    initComponents: kind.inherit(function(sup) {
        return function() {

            // Create a scroller as control parent.
            this.createScroller();

            // Create components
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            this.anchor = utils.clone(this.anchor);
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.fitChanged();
        };
    }),

    /**
     * Creates a scroller control to serve as controlParent. All components will
     * be rendered into this scroll component.
     *
     * @private
     */
    createScroller: function() {
        var scroller;

        scroller                = this.createComponent({
            name: "scroller",
            kind: this.scrollerKind
        }, this.scrollerOptions || {});
        !scroller.controlParent && scroller.discoverControlParent();
        this.controlParent      = scroller.controlParent || scroller;
    },

    /**
     * Returns the scroller instance.
     *
     * @return {enyo-ui.Scroller}  The scroller.
     * @public
     */
    getScroller: function() {
        return this.$.scroller;
    },

    // Render, Resize, Reflow
    // --------------------------------

    /**
     * @private
     */
    fitChanged: function() {
        var fit = this.get("fit");

        if (fit) {
            !this.hasClass("enyo-fit") && this.addClass("enyo-fit");
        }
        else {
            while (this.hasClass("enyo-fit")) {
                this.removeClass("enyo-fit");
            }
        }

        this.$.scroller.set("fit", fit);
    },

    /**
     * Reflowing and re-arranging a large set of controls is costly. Instead we
     * clear the repeater and re-create items as needed.
     *
     * @method
     * @private
     */
    reflow: kind.inherit(function(sup) {
        return function() {
            this.clearAnchorOffset();
            sup.apply(this, arguments);
        };
    }),

    // Draw
    // --------------------------------

    /**
     * Respond to changed [scrollerDimensions]{@link
     * enyo-ui.ScrollRepeater#scrollerDimensions} by re-drawing as needed.
     *
     * @param {Object}  oldDimensions  The old dimensions
     * @protected
     */
    scrollerDimensionsChanged: function(oldDimensions) {
        var newDimensions = this.get("scrollerDimensions") || {};

        oldDimensions = oldDimensions || {};

        if (newDimensions.scrollerWidth > 0
            && newDimensions.scrollerHeight > 0
            && (oldDimensions.scrollerWidth !== newDimensions.scrollerWidth
                || oldDimensions.scrollerHeight !== newDimensions.scrollerHeight
                || oldDimensions.contentWidth !== newDimensions.contentWidth
                || oldDimensions.contentHeight !== newDimensions.contentHeight
            )
        ) {
            this.drawing ? this.set("needsDraw", true) : this.draw();
        }
    },

    // Arrange
    // --------------------------------

    /**
     * Arranges existing children to the right position, this should be fast and
     * should not introduce reflows. Arranging will be deferred if the repeater
     * is not absolutely showing.
     *
     * @return {boolean} `true` if the flow of the arrangement changed, `false`
     *                   otherwise.
     * @protected
     */
    arrange: kind.inherit(function(sup) {
        return function() {
            var scroller,
                arrangementSize,
                flowChanged;

            // Normal repeater arrangement applies
            flowChanged = sup.apply(this, arguments);

            // If the arrangement changed, we need to snap to the scroll-anchor.
            // This keeps a designated item visible within the repeater.
            if (flowChanged && this.absoluteShowing) {
                scroller        = this.getScroller();
                arrangementSize = this.get("arrangementSize");

                if ((arrangementSize.width && scroller.horizontal)
                    || (arrangementSize.height && scroller.vertical)) {
                    this.snapToAnchor();
                }
            }

            return flowChanged;
        };
    }),

    // Scroll
    // --------------------------------

    /**
     * @private
     */
    snappingToAnchor: false,

    /**
     * Stops arrangement when a scroll interaction starts.
     *
     * @private
     */
    onScrollStart: function() {
        this.drawing && this.stopDrawing();
    },

    /**
     * Updates scroll anchor
     *
     * @private
     */
    onScroll: function() {
        // Chrome sometimes doesn't update scroll position entirely
        // synchronously, causing anchor updates after applying an anchor to be
        // inaccurate. Luckily we don't need to re-evaluate the anchor so we can
        // just skip this step.
        if (!this.snappingToAnchor) {
            this.updateAnchor();
        }
        else {
            this.snappingToAnchor = false;
        }
    },

    /**
     * Updates the arrangement when a scroll interaction stops.
     *
     * @private
     */
    onScrollStop: function() {
        this.continueDrawing();
    },

    /**
     * Figures out which index is currently located closest to the top-left
     * corner of the viewport as well as it's offset relative to the top-left
     * corner of the viewport.
     *
     * By default we assume the top-left corner of the childContainer aligns
     * with the top-lef corner of the scroller. Override this method if this is
     * not the case.
     *
     * @public
     */
    updateAnchor: function() {
        var scrollPosition = this.getScroller().scrollPosition,
            index,
            offsetLeft,
            offsetTop;

        index      = this.arranger.getNearestIndex(
            scrollPosition.scrollLeft,
            scrollPosition.scrollTop
        );
        offsetLeft = scrollPosition.scrollLeft - this.arranger.getScrollLeft(index);
        offsetTop  = scrollPosition.scrollTop - this.arranger.getScrollTop(index);

        this.set("anchor", {
            index:      index,
            offsetLeft: offsetLeft,
            offsetTop:  offsetTop
        });
    },

    /**
     * Leaves the anchor index intact, but removes any additional scroll offset.
     *
     * @private
     */
    clearAnchorOffset: function() {
        this.set("anchor", {
            index:      this.anchor.index,
            offsetLeft: 0,
            offsetTop:  0
        });
    },

    /**
     * Snaps the repeater to the current anchor.
     *
     * By default we assume the top-left corner of the childContainer aligns
     * with the top-lef corner of the scroller. Override this method if this is
     * not the case.
     *
     * @public
     */
    snapToAnchor: function() {
        var scroller       = this.getScroller(),
            arranger       = this.arranger,
            anchor         = this.anchor,
            scrollPosition = scroller.scrollPosition,
            scrollLeft,
            scrollTop;

        if (!scroller.scrolling) {
            scrollLeft = arranger.getScrollLeft(anchor.index) + anchor.offsetLeft;
            scrollTop  = arranger.getScrollTop(anchor.index) + anchor.offsetTop;

            if (scrollPosition.scrollLeft !== scrollLeft
                || scrollPosition.scrollTop !== scrollTop) {
                this.snappingToAnchor = true;
                scroller.snapTo(scrollLeft, scrollTop);
            }
        }
    },

    /**
     * Scrolls the repeater to the provided index.
     *
     * By default we assume the top-left corner of the childContainer aligns
     * with the top-lef corner of the scroller. Override this method if this is
     * not the case.
     *
     * @param {number}  index  The index
     * @public
     */
    scrollToIndex: function(index) {
        var scroller = this.getScroller(),
            arranger = this.arranger;

        scroller.scrollTo(
            arranger.getScrollLeft(index),
            arranger.getScrollTop(index)
        );
    },

    /**
     * Snaps the repeater to the provided index.
     *
     * By default we assume the top-left corner of the childContainer aligns
     * with the top-lef corner of the scroller. Override this method if this is
     * not the case.
     *
     * @param {number}  index  The index
     * @public
     */
    snapToIndex: function(index) {
        var scroller = this.getScroller(),
            arranger = this.arranger;

        scroller.snapTo(
            arranger.getScrollLeft(index),
            arranger.getScrollTop(index)
        );
    }

});

/**
 * This method allows enyo.kind to extend the `scrollerOptions` hash when creating
 * super kinds.
 *
 * @param    {Function}  ctor   The constructor
 * @param    {Object}    props  The properties
 * @memberof Repeater
 * @static
 * @private
 */
ScrollRepeater.concat = function(ctor, props) {
    var proto = ctor.prototype || ctor,
        scrollerOptions;

    if (props.scrollerOptions) {
        scrollerOptions       = proto.scrollerOptions ? utils.clone(proto.scrollerOptions) : {};
        proto.scrollerOptions = utils.mixin(scrollerOptions, props.scrollerOptions);
        delete props.scrollerOptions;
    }
};
